// ─────────────────────────────────────────────────────────────────────────────
// Location Emails Reducer
// ─────────────────────────────────────────────────────────────────────────────
// State interface, initial state, and reducer for the locationEmails slice.
// State is partitioned by location_id so the cache survives navigating between
// sibling locations within the same SPA session.
// ─────────────────────────────────────────────────────────────────────────────

import { Action, createReducer, on } from '@ngrx/store';
import { ILocationEmail, IStructuredApiError } from '../models/location-email.models';
import * as locationEmailsActions from './location-emails.actions';

// ── State interface ──────────────────────────────────────────────────────────

export interface LocationEmailsBucket {
  erp: ILocationEmail[];
  volta: ILocationEmail[];
}

export interface LocationEmailsState {
  byLocationId: Record<number, LocationEmailsBucket>;
  byCustomerId: Record<number, LocationEmailsBucket>;
  loadingLocationId: number | null;
  loadingCustomerId: number | null;
  error: string | null;
  lastMutationError: IStructuredApiError | null;
}

// ── Initial state ────────────────────────────────────────────────────────────

export const initialLocationEmailsState: LocationEmailsState = {
  byLocationId: {},
  byCustomerId: {},
  loadingLocationId: null,
  loadingCustomerId: null,
  error: null,
  lastMutationError: null,
};

// ── Helpers ──────────────────────────────────────────────────────────────────

const emptyBucket = (): LocationEmailsBucket => ({ erp: [], volta: [] });

const partition = (rows: ILocationEmail[]): LocationEmailsBucket => {
  const erp: ILocationEmail[] = [];
  const volta: ILocationEmail[] = [];
  for (const r of rows) {
    if (r.source === 'erp') erp.push(r);
    else volta.push(r);
  }
  return { erp, volta };
};

// The merged UI loads exactly one company bucket at a time (the focused
// customer). Create targets that customer's bucket directly so a location's
// first email shows immediately; update/delete match by id across buckets.
// erp rows are never touched by mutations.

const prependVoltaToCompanyBucket = (
  byCustomerId: Record<number, LocationEmailsBucket>,
  customerId: number,
  email: ILocationEmail,
): Record<number, LocationEmailsBucket> => {
  const bucket = byCustomerId[customerId] ?? emptyBucket();
  return {
    ...byCustomerId,
    [customerId]: { erp: bucket.erp, volta: [email, ...bucket.volta] },
  };
};

const mapVoltaInCompanyBuckets = (
  byCustomerId: Record<number, LocationEmailsBucket>,
  email: ILocationEmail,
): Record<number, LocationEmailsBucket> => {
  const next: Record<number, LocationEmailsBucket> = { ...byCustomerId };
  for (const [customerId, bucket] of Object.entries(byCustomerId)) {
    if (bucket.volta.some((r) => r.id === email.id)) {
      next[Number(customerId)] = { erp: bucket.erp, volta: bucket.volta.map((r) => (r.id === email.id ? email : r)) };
    }
  }
  return next;
};

const removeVoltaFromCompanyBuckets = (
  byCustomerId: Record<number, LocationEmailsBucket>,
  id: number,
): Record<number, LocationEmailsBucket> => {
  const next: Record<number, LocationEmailsBucket> = { ...byCustomerId };
  for (const [customerId, bucket] of Object.entries(byCustomerId)) {
    if (bucket.volta.some((r) => r.id === id)) {
      next[Number(customerId)] = { erp: bucket.erp, volta: bucket.volta.filter((r) => r.id !== id) };
    }
  }
  return next;
};

// ── Reducer ──────────────────────────────────────────────────────────────────

const featureReducer = createReducer(
  initialLocationEmailsState,

  on(locationEmailsActions.LoadLocationEmails, (state, { locationId }) => ({
    ...state,
    loadingLocationId: locationId,
    error: null,
    lastMutationError: null,
  })),

  on(locationEmailsActions.LoadLocationEmailsSuccess, (state, { locationId, response }) => ({
    ...state,
    loadingLocationId: null,
    byLocationId: {
      ...state.byLocationId,
      [locationId]: partition(response.data || []),
    },
  })),

  on(locationEmailsActions.LoadLocationEmailsError, (state, { error }) => ({
    ...state,
    loadingLocationId: null,
    error,
  })),

  on(locationEmailsActions.LoadCompanyLocationEmails, (state, { customerId }) => ({
    ...state,
    loadingCustomerId: customerId,
    error: null,
    lastMutationError: null,
  })),

  on(locationEmailsActions.LoadCompanyLocationEmailsSuccess, (state, { customerId, response }) => ({
    ...state,
    loadingCustomerId: null,
    byCustomerId: {
      ...state.byCustomerId,
      [customerId]: partition(response.data || []),
    },
  })),

  on(locationEmailsActions.LoadCompanyLocationEmailsError, (state, { error }) => ({
    ...state,
    loadingCustomerId: null,
    error,
  })),

  on(locationEmailsActions.CreateLocationEmailSuccess, (state, { email, customerId }) => {
    const existing = state.byLocationId[email.location_id] ?? emptyBucket();
    return {
      ...state,
      lastMutationError: null,
      byLocationId: {
        ...state.byLocationId,
        [email.location_id]: {
          erp: existing.erp,
          volta: [email, ...existing.volta],
        },
      },
      byCustomerId: prependVoltaToCompanyBucket(state.byCustomerId, customerId, email),
    };
  }),

  on(locationEmailsActions.CreateLocationEmailError, (state, { error }) => ({
    ...state,
    lastMutationError: error,
  })),

  on(locationEmailsActions.UpdateLocationEmailSuccess, (state, { email }) => {
    const existing = state.byLocationId[email.location_id] ?? emptyBucket();
    return {
      ...state,
      lastMutationError: null,
      byLocationId: {
        ...state.byLocationId,
        [email.location_id]: {
          erp: existing.erp,
          volta: existing.volta.map((row) => (row.id === email.id ? email : row)),
        },
      },
      byCustomerId: mapVoltaInCompanyBuckets(state.byCustomerId, email),
    };
  }),

  on(locationEmailsActions.UpdateLocationEmailError, (state, { error }) => ({
    ...state,
    lastMutationError: error,
  })),

  on(locationEmailsActions.DeleteLocationEmailSuccess, (state, { id, locationId }) => {
    const existing = state.byLocationId[locationId];
    const byCustomerId = removeVoltaFromCompanyBuckets(state.byCustomerId, id);
    if (!existing) return { ...state, lastMutationError: null, byCustomerId };
    return {
      ...state,
      lastMutationError: null,
      byLocationId: {
        ...state.byLocationId,
        [locationId]: {
          erp: existing.erp,
          volta: existing.volta.filter((row) => row.id !== id),
        },
      },
      byCustomerId,
    };
  }),

  on(locationEmailsActions.DeleteLocationEmailError, (state, { error }) => ({
    ...state,
    lastMutationError: error,
  })),

  on(locationEmailsActions.ClearLocationEmailsError, (state) => ({
    ...state,
    lastMutationError: null,
    error: null,
  })),
);

export function locationEmailsReducer(state: LocationEmailsState | undefined, action: Action) {
  return featureReducer(state, action);
}
